import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import './HighchartsTemperatureGraph.less'

export default function HighchartsTemperatureGraph ({ className, data = [], height = 120, margin = {} }) {
  const chartRef = useRef(null)
  const chartInstanceRef = useRef(null)
  const [highchartsLoaded, setHighchartsLoaded] = useState(false)

  // Load Highcharts script if not already loaded
  useEffect(() => {
    if (window.Highcharts) {
      setHighchartsLoaded(true)
      return
    }

    const script = document.createElement('script')
    script.src = '/vendor/highcharts/highcharts.js'
    script.async = true
    script.onload = () => {
      setHighchartsLoaded(true)
    }
    script.onerror = () => {
      console.error('Failed to load Highcharts')
    }
    document.head.appendChild(script)

    return () => {
      // Don't remove the script on unmount as it might be used by other components
    }
  }, [])

  useEffect(() => {
    // Ensure Highcharts is loaded
    if (!highchartsLoaded || !window.Highcharts || !chartRef.current || data.length === 0) {
      return
    }

    // Get CSS variable values
    const brandColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--brandColor').trim()
    const fillColor = '#ebf7ff'

    // Transform data for Highcharts
    const categories = data.map(d => d.label)
    const values = data.map(d => d.value)

    // Split data into daytime and evening series based on hour
    // Include boundary points in both series for smooth line transitions
    const createDayNightSeries = (data) => {
      const daytimeValues = []
      const eveningValues = []

      data.forEach((point, index) => {
        const hour = new Date(point.time * 1000).getHours()
        const isDaytime = hour >= 0 && hour < 12

        // Check previous and next hours to detect boundaries
        const prevHour = index > 0
          ? new Date(data[index - 1].time * 1000).getHours()
          : null
        const nextHour = index < data.length - 1
          ? new Date(data[index + 1].time * 1000).getHours()
          : null

        // Determine if this point needs to be in both series
        // - 12pm (hour 12): Include in daytime if previous hour was daytime (11am)
        // - 12am (hour 0): Include in evening if previous hour was evening (11pm)
        const includeInDaytime = isDaytime || (hour === 12 && prevHour !== null && prevHour < 12)
        const includeInEvening = !isDaytime || (hour === 0 && prevHour !== null && prevHour >= 12)

        daytimeValues.push(includeInDaytime ? point.value : null)
        eveningValues.push(includeInEvening ? point.value : null)
      })

      return {
        daytime: daytimeValues,
        evening: eveningValues
      }
    }

    const seriesData = createDayNightSeries(data)

    // Calculate margins (Highcharts uses [top, right, bottom, left])
    // Increase top margin for weather icons + temperature labels
    // Increase bottom margin to make room for x-axis labels
    const chartMargin = [
      60, // Room for weather icons (30px) + temperature labels (~20px) + spacing
      margin.right || 10,
      25, // More space for labels below axis
      margin.left || 10
    ]

    // Destroy existing chart if it exists
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy()
    }

    // Create new chart
    chartInstanceRef.current = window.Highcharts.chart(chartRef.current, {
      chart: {
        type: 'area',
        height: height,
        margin: chartMargin,
        backgroundColor: 'transparent',
        spacing: [0, 0, 0, 0]
      },
      title: {
        text: null
      },
      subtitle: {
        text: null
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      },
      legend: {
        enabled: false
      },
      xAxis: {
        categories: categories,
        gridLineWidth: 0,
        gridLineColor: '#f0f0f0',
        lineWidth: 0,
        lineColor: '#e0e0e0',
        tickColor: '#e0e0e0',
        // tickLength: -10, // Negative value makes ticks extend upward into plot area
        tickWidth: 0,
        tickInterval: 1,
        labels: {
          enabled: true,
          y: 15, // Position labels below the axis
          style: {
            color: '#666',
            fontSize: '10px',
            fontWeight: '400'
          }
        }
      },
      yAxis: {
        title: {
          text: null
        },
        gridLineWidth: 0,
        labels: {
          enabled: false
        }
      },
      tooltip: {
        enabled: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderWidth: 0,
        style: {
          color: '#fff'
        },
        formatter: function () {
          return '<b>' + this.y + '°</b><br/>' + this.x
        }
      },
      plotOptions: {
        area: {
          dataLabels: {
            useHTML: true,
            align: 'center',
            crop: false,
            overflow: 'allow',
            y: -10,
            style: {
              textOutline: 'none'
            }
          },
          marker: {
            enabled: false
          },
          lineWidth: 2,
          states: {
            hover: {
              lineWidth: 2
            }
          },
          threshold: null
        }
      },
      series: [
        {
          name: 'Daytime',
          data: seriesData.daytime,
          lineColor: brandColor,
          fillColor: '#ebf7ff', // Lighter blue for daytime (12am-12pm)
          animation: false,
          enableMouseTracking: false,
          dataLabels: {
            enabled: true,
            formatter: function () {
              const dataIndex = this.point.index
              const hour = new Date(data[dataIndex].time * 1000).getHours()
              const isDaytime = hour >= 0 && hour < 12

              // Only show labels for daytime hours (avoid duplicates at boundaries)
              if (!isDaytime) return null

              const icon = data[dataIndex]?.icon || 'clear-day'
              return `
                <div style="text-align: center; width: 30px;">
                  <div class="weather-icon ${icon}" style="height: 25px; width: 30px; margin: 0 auto;"></div>
                  <div style="font-size: 12px; font-weight: 400; color: #333; margin-top: 6px;">${this.y}°</div>
                </div>
              `
            }
          }
        },
        {
          name: 'Evening',
          data: seriesData.evening,
          lineColor: brandColor, // Same line color
          fillColor: '#c4e2f7', // Darker blue for evening (12pm-12am)
          animation: false,
          enableMouseTracking: false,
          dataLabels: {
            enabled: true,
            formatter: function () {
              const dataIndex = this.point.index
              const hour = new Date(data[dataIndex].time * 1000).getHours()
              const isDaytime = hour >= 0 && hour < 12

              // Only show labels for evening hours (avoid duplicates at boundaries)
              if (isDaytime) return null

              const icon = data[dataIndex]?.icon || 'clear-day'
              return `
                <div style="text-align: center; width: 30px;">
                  <div class="weather-icon ${icon}" style="height: 25px; width: 30px; margin: 0 auto;"></div>
                  <div style="font-size: 12px; font-weight: 400; color: #333; margin-top: 6px;">${this.y}°</div>
                </div>
              `
            }
          }
        }
      ]
    })

    // Cleanup on unmount
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy()
        chartInstanceRef.current = null
      }
    }
  }, [data, height, margin, highchartsLoaded])

  return (
    <div className={classNames('common-highcharts-temperature-graph', className)}>
      <div ref={chartRef} />
    </div>
  )
}

HighchartsTemperatureGraph.displayName = 'HighchartsTemperatureGraph'
